import { useCallback, useEffect, useState } from 'react';
import { CreditCard, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';
import Toast from './Toast';

const STATUS_COPY = {
  NON_CONFIGURE: {
    title: 'Configurez Stripe pour recevoir directement vos paiements en ligne.',
    cta: 'Configurer mes paiements',
    action: 'onboarding',
    tone: 'neutral',
  },
  EN_ATTENTE: {
    title: 'Votre configuration Stripe n’est pas terminée.',
    cta: 'Continuer la configuration',
    action: 'onboarding',
    tone: 'warning',
  },
  ACTIF: {
    title: 'Votre compte Stripe est actif.',
    cta: 'Gérer mes informations Stripe',
    action: 'update',
    tone: 'success',
  },
  RESTREINT: {
    title: 'Stripe demande des informations supplémentaires.',
    cta: 'Mettre à jour mon compte Stripe',
    action: 'update',
    tone: 'danger',
  },
};

function StatusBadge({ status }) {
  const styles = {
    NON_CONFIGURE: 'bg-gray-100 text-gray-700',
    EN_ATTENTE: 'bg-amber-100 text-amber-800',
    ACTIF: 'bg-green-100 text-green-800',
    RESTREINT: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || styles.NON_CONFIGURE}`}>
      {status}
    </span>
  );
}

/**
 * Panneau commerçant — onboarding Stripe Connect (Phase B).
 * Aucun IBAN / KYC / donnée bancaire saisie ici.
 */
export default function StripeConnectPanel({ commercantId, isSuperAdmin = false, onStatusChange }) {
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    if (!commercantId) return;
    setLoading(true);
    try {
      const data = await apiService.getStripeConnectStatus(commercantId);
      setStatus(data);
      onStatusChange?.(data);
    } catch (err) {
      console.error('[StripeConnectPanel]', err);
      toast.error(err?.message || 'Impossible de charger le statut Stripe Connect');
    } finally {
      setLoading(false);
    }
  }, [commercantId, onStatusChange]);

  useEffect(() => {
    load();
  }, [load]);

  const openLink = async (kind) => {
    setBusy(true);
    try {
      const link =
        kind === 'update'
          ? await apiService.createStripeConnectUpdateLink(commercantId)
          : await apiService.createStripeConnectOnboardingLink(commercantId);
      if (!link?.url) {
        throw new Error('Lien Stripe indisponible');
      }
      window.location.href = link.url;
    } catch (err) {
      toast.error(err?.message || 'Impossible d’ouvrir Stripe');
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const data = await apiService.syncStripeConnect(commercantId);
      setStatus(data);
      onStatusChange?.(data);
      toast.success('Statut Stripe synchronisé');
    } catch (err) {
      toast.error(err?.message || 'Synchronisation impossible');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-500">Chargement Stripe Connect…</p>
      </div>
    );
  }

  const code = status?.status || 'NON_CONFIGURE';
  const copy = STATUS_COPY[code] || STATUS_COPY.NON_CONFIGURE;
  const Icon =
    code === 'ACTIF' ? CheckCircle2 : code === 'RESTREINT' ? AlertTriangle : code === 'EN_ATTENTE' ? Clock : CreditCard;
  const iconTone =
    code === 'ACTIF'
      ? 'bg-green-50 text-green-600'
      : code === 'RESTREINT'
        ? 'bg-red-50 text-red-600'
        : code === 'EN_ATTENTE'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-orange-50 text-orange-600';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 rounded-lg p-2 ${iconTone}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-gray-900">Paiements Stripe</h3>
              <StatusBadge status={code} />
            </div>
            <p className="text-sm text-gray-600 mt-1">{copy.title}</p>
            <p className="text-xs text-gray-400 mt-2">
              Les informations bancaires et le KYC sont gérés exclusivement par Stripe.
              Clickorico ne collecte aucun IBAN.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => openLink(copy.action)}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
        >
          <ExternalLink className="h-4 w-4" />
          {copy.cta}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={sync}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          Synchroniser
        </button>
      </div>

      {isSuperAdmin && status && (
        <StripeConnectAdminDetails
          status={status}
          commercantId={commercantId}
          busy={busy}
          setBusy={setBusy}
          toast={toast}
          onUpdated={(data) => {
            setStatus(data);
            onStatusChange?.(data);
          }}
        />
      )}
    </div>
  );
}

function humanizeRequirements(requirements) {
  if (!requirements || typeof requirements !== 'object') return [];
  const lines = [];
  if (requirements.disabled_reason) {
    lines.push(`Restriction : ${requirements.disabled_reason}`);
  }
  for (const key of ['currently_due', 'past_due', 'eventually_due', 'pending_verification']) {
    const list = requirements[key];
    if (Array.isArray(list) && list.length > 0) {
      lines.push(`${key.replace(/_/g, ' ')} : ${list.slice(0, 8).join(', ')}${list.length > 8 ? '…' : ''}`);
    }
  }
  if (Array.isArray(requirements.errors)) {
    for (const e of requirements.errors.slice(0, 5)) {
      lines.push(e.reason || e.code || 'Erreur Stripe');
    }
  }
  return lines;
}

function StripeConnectAdminDetails({ status, commercantId, busy, setBusy, toast, onUpdated }) {
  const reqLines = humanizeRequirements(status.requirements);

  const togglePaiementEnLigne = async (next) => {
    setBusy(true);
    try {
      await apiService.updateCommercant(commercantId, { paiementEnLigne: next });
      toast.success(next ? 'Paiement en ligne activé' : 'Paiement en ligne désactivé');
      onUpdated?.({ ...status, paiementEnLigne: next });
    } catch (err) {
      toast.error(err?.message || 'Mise à jour impossible');
    } finally {
      setBusy(false);
    }
  };

  const relancer = async () => {
    setBusy(true);
    try {
      const link = await apiService.createStripeConnectOnboardingLink(commercantId);
      if (link?.url) window.location.href = link.url;
      else throw new Error('Lien indisponible');
    } catch (err) {
      toast.error(err?.message || 'Impossible de rouvrir le parcours Stripe');
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-4 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Supervision super admin</p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        <div>
          <dt className="text-gray-500">Paiements (charges)</dt>
          <dd className="font-medium">{status.chargesEnabled ? 'Activés' : 'Désactivés'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Virements (payouts)</dt>
          <dd className="font-medium">{status.payoutsEnabled ? 'Activés' : 'Désactivés'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Onboarding terminé</dt>
          <dd className="font-medium">{status.detailsSubmitted ? 'Oui' : 'Non'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Dernière synchronisation</dt>
          <dd className="font-medium">
            {status.onboardingUpdatedAt
              ? new Date(status.onboardingUpdatedAt).toLocaleString('fr-FR')
              : '—'}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-gray-500">Compte Connect</dt>
          <dd className="font-mono text-xs text-gray-700">
            {status.stripeConnectAccountId
              ? `${status.stripeConnectAccountId.slice(0, 8)}…`
              : 'Non associé'}
          </dd>
        </div>
      </dl>

      {reqLines.length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-100 p-3">
          <p className="text-xs font-medium text-amber-900 mb-1">Exigences Stripe</p>
          <ul className="text-xs text-amber-800 list-disc pl-4 space-y-1">
            {reqLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={relancer}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Rouvrir le parcours Stripe
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => togglePaiementEnLigne(!status.paiementEnLigne)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {status.paiementEnLigne ? 'Désactiver paiementEnLigne' : 'Activer paiementEnLigne'}
        </button>
      </div>
      <p className="text-xs text-gray-400">
        Rouvre le parcours de vérification et de configuration Stripe Connect.
        Interdit ici : saisie IBAN, KYC, virements manuels, modification bancaire.
      </p>
    </div>
  );
}
